'use client';

import { PropsWithChildren, useState } from 'react';

import { PopupBlockedModal } from '../PopupBlockedModal/PopupBlockedModal';
import { PopupRefocusModal } from '../PopupRefocusModal/PopupRefocusModal';
import { usePage } from '../Providers/PageProvider';
import { useTranslation } from 'react-i18next';
import { DonateButton } from '../DonateButton/DonateButton';
import { isMobileDevice } from '../../utils/mobile';

type DonatePopupButtonProps = {
  id?: string;
  className?: string;
  colorVariant?: 'brand' | 'primary';
} & PropsWithChildren;

export const DonatePopupButton: React.FC<DonatePopupButtonProps> = ({
  id,
  children,
  className,
  colorVariant = 'primary',
}) => {
  const { t } = useTranslation();
  const { url } = usePage();

  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const [popupBlockedModalOpen, setPopupBlockedModalOpen] = useState(false);

  function handleClick(event: React.MouseEvent) {
    try {
      const donateUrl = `${url}/donate${buildQueryString(true)}`;

      if (!isMobileDevice()) {
        showCheckoutPopup(donateUrl);
        event.preventDefault();
      }
    } catch (error) {
      // Do nothing, fallback to the link's default behaviour
    }
  }

  function showCheckoutPopup(href: string) {
    const screenX = window.screenX;
    const screenY = window.screenY;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const popupHeight = 640;
    const popupWidth = 938;

    const popupLeft = screenX + (screenWidth / 2 - popupWidth / 2);
    const popupTop = screenY + (screenHeight / 2 - popupHeight / 2);
    const windowFeatures = `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop}`;

    const urlParams = new URLSearchParams(window.location.search);
    const popupWindow =
      urlParams.get('popupBlocked') === 'true'
        ? null
        : window.open(href, '_blank', windowFeatures);

    setPopupWindow(popupWindow);

    if (popupWindow) {
      setPopupWindow(popupWindow);
      popupWindow?.focus();
    } else {
      setPopupBlockedModalOpen(true);
    }
  }

  function buildQueryString(showEmbeddedCheckout?: boolean) {
    const urlParams = new URLSearchParams(window.location.search);
    const donateUrlParams = new URLSearchParams();

    if (showEmbeddedCheckout) {
      donateUrlParams.append('checkoutMode', 'Headless');
    }

    const utmSource = urlParams.get('utm_source');
    if (utmSource) {
      donateUrlParams.append('reference', utmSource);
    }

    return donateUrlParams.toString() ? `?${donateUrlParams.toString()}` : '';
  }

  return (
    <>
      <DonateButton
        onClick={handleClick}
        colorVariant={colorVariant}
        className={className}
        id={id}
      >
        {children ?? t('giveNow')}
      </DonateButton>
      <PopupRefocusModal
        popupWindow={popupWindow}
        onClose={() => setPopupWindow(null)}
      />
      {popupBlockedModalOpen && (
        <PopupBlockedModal
          open
          onClose={() => setPopupBlockedModalOpen(false)}
          checkoutUrl={`${url}/donate${buildQueryString(false)}`}
        />
      )}
    </>
  );
};
